/*
 * © 2026 Renishaw plc. All rights reserved.
 * This source file is the confidential property and copyright of Renishaw plc
 * Reproduction or transmission in whole or in part, in any form or
 * by any means, electronic, mechanical or otherwise, is prohibited
 * without the prior written consent of the copyright owner.
 */
import { JobPresentation } from "@/presentation/Job";
import {
  createMetadataFilterSet,
  isEventInJobTimeRange,
  MetadataEvent,
} from "./metadataFilterUtils";

export function filterJobsByJobType(
  jobs: JobPresentation[],
  jobTypeFilters: string[]
): JobPresentation[] {
  if (!jobTypeFilters.length) return jobs;
  return jobs.filter((job) => jobTypeFilters.includes(job.name));
}

export function filterJobsByJobName(
  jobs: JobPresentation[],
  jobNames: string[] | undefined
): JobPresentation[] {
  if (!jobNames?.length) return jobs;
  return jobs.filter((job) => jobNames.includes(job.name));
}

export function filterJobsByVerdict(
  jobs: JobPresentation[],
  verdicts: string[] | undefined
): JobPresentation[] {
  if (!verdicts?.length) return jobs;
  return jobs.filter((job) => verdicts.includes(job.verdict));
}

export function filterJobsByStatus(
  jobs: JobPresentation[],
  statuses: string[] | undefined
): JobPresentation[] {
  if (!statuses?.length) return jobs;

  const result: JobPresentation[] = [];

  if (statuses.includes("Completed")) {
    result.push(
      ...jobs.filter((job) => job.status.toLowerCase() === "completed")
    );
  }
  if (statuses.includes("Incomplete")) {
    result.push(
      ...jobs.filter((job) => job.status.toLowerCase() !== "completed")
    );
  }

  return result;
}

export function filterJobsByMetaData(
  jobs: JobPresentation[],
  metaData: Record<string, string[]> | undefined,
  jobMetadata: MetadataEvent[]
): JobPresentation[] {
  if (!metaData || Object.keys(metaData).length === 0) return jobs;
  const metadataFilterSet = createMetadataFilterSet(metaData);

  const activeJobMetadata = jobMetadata.filter(
    (event) =>
      event?.value !== undefined &&
      event?.value !== null &&
      !!event?.created &&
      metadataFilterSet.has(String(event.value))
  );

  return jobs.filter((job) =>
    activeJobMetadata.some((event: any) =>
      isEventInJobTimeRange(event.created, job.start, job.end)
    )
  );
}
